import React from 'react';
import { Target, Eye, Award, Users, Zap, Shield } from 'lucide-react';
import './About.css';

const About = () => {
  const values = [
    { icon: Award, title: 'Excellence', description: 'We strive for excellence in every project, delivering solutions that exceed expectations.' },
    { icon: Zap, title: 'Innovation', description: 'Constantly pushing boundaries to bring cutting-edge technology solutions to our clients.' },
    { icon: Users, title: 'Collaboration', description: 'Working closely with clients to understand their needs and deliver tailored solutions.' },
    { icon: Shield, title: 'Integrity', description: 'Building trust through transparency, honesty, and ethical business practices.' }
  ];

  const team = [
    { name: 'Technical Team', role: 'Firmware & Hardware Engineers', description: 'Expert engineers with years of experience in embedded systems development.' },
    { name: 'Design Team', role: 'PCB & Product Designers', description: 'Creative designers who transform concepts into manufacturable products.' },
    { name: 'Quality Team', role: 'QA & Testing Specialists', description: 'Dedicated professionals ensuring the highest quality standards.' }
  ];

  return (
    <div className="about-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>About Us</h1>
          <p>Learn more about Signature Embedded Systems and our journey</p>
          <p className="tagline">INNOVATION IN EVERY TRACE</p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="company-overview section">
        <div className="container">
          <div className="overview-grid">
            <div className="overview-content">
              <h2>Who We Are</h2>
              <p>
                <strong>Signature Embedded Systems</strong> is a leading provider of embedded solutions,
                headquartered in Hyderabad, India. We specialize in firmware development, hardware design,
                PCB design, and IoT solutions, serving clients across various industries worldwide.
              </p>
              <p>
                Founded with a vision to provide cost-effective, innovative smart solutions, we have
                grown to become a trusted partner for businesses seeking to leverage embedded technology
                for their digital transformation journey.
              </p>
              <p>
                Our team of experienced engineers and designers combines technical expertise with
                creative problem-solving to deliver solutions that drive real business value.
              </p>
            </div>
            <div className="overview-image">
              <div className="image-placeholder">
                <img
                  src="/images/logo.jpg"
                  alt="Signature Embedded Systems - Innovation in Every Trace"
                  className="overview-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="vision-mission section bg-light">
        <div className="container">
          <div className="vm-grid">
            <div className="vm-card">
              <div className="vm-icon">
                <Eye size={40} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To be the leading provider of innovative embedded solutions, empowering businesses
                worldwide with cost-effective, smart technology that drives progress and transforms industries.
              </p>
            </div>
            <div className="vm-card">
              <div className="vm-icon">
                <Target size={40} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To deliver cutting-edge embedded systems and IoT solutions that help our clients
                achieve their digital transformation goals, while maintaining the highest standards
                of quality, innovation, and customer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="core-values section">
        <div className="container">
          <div className="section-title">
            <h2>Our Core Values</h2>
            <p>The principles that guide everything we do</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card card">
                <div className="value-icon">
                  <value.icon size={32} />
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section section bg-light">
        <div className="container">
          <div className="section-title">
            <h2>Our Team</h2>
            <p>Meet the experts behind our solutions</p>
          </div>
          <div className="team-grid">
            {team.map((member, index) => (
              <div key={index} className="team-card card">
                <div className="team-avatar">
                  <Users size={40} />
                </div>
                <h3>{member.name}</h3>
                <span className="team-role">{member.role}</span>
                <p>{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us section">
        <div className="container">
          <div className="section-title">
            <h2>Why Choose Us</h2>
            <p>What sets Signature Embedded Systems apart</p>
          </div>
          <div className="why-us-grid">
            <div className="why-us-item">
              <span className="why-us-number">01</span>
              <h4>Expertise</h4>
              <p>Years of experience in embedded systems development across multiple industries.</p>
            </div>
            <div className="why-us-item">
              <span className="why-us-number">02</span>
              <h4>Quality</h4>
              <p>Rigorous testing and quality assurance processes ensure reliable products.</p>
            </div>
            <div className="why-us-item">
              <span className="why-us-number">03</span>
              <h4>Support</h4>
              <p>Dedicated support team available to assist you throughout the project lifecycle.</p>
            </div>
            <div className="why-us-item">
              <span className="why-us-number">04</span>
              <h4>Cost-Effective</h4>
              <p>Competitive pricing without compromising on quality or performance.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
